import { useEffect, useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { ImageCompany } from '@/routes/_app/-components/assets/image-company'
import { cn, getInitials } from '@/lib/utils'
import type { ProfileType } from 'shared'
import { useLingui } from '@lingui/react/macro'
import { cva, type VariantProps } from 'class-variance-authority'
import { NAVIGATION_ICONS } from './navigation-config'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// Runs before paint on the client, falls back to useEffect during SSR (where useLayoutEffect warns).
// We reveal already-`complete` (cached/SSR-loaded) images here so they appear on the FIRST paint
// instead of one frame later — otherwise the colored initials flash on every mount.
const useIsomorphicLayoutEffect = typeof document !== 'undefined' ? useLayoutEffect : useEffect

// For avatar use the expertly-crafted default color palette for tailwind: https://tailwindcss.com/docs/customizing-colors
const personColors = [
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
  '#84cc16', // Lime
  '#22c55e', // Green
  '#10b981', // Emerald
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#0ea5e9', // Sky
  '#3b82f6', // Blue
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#d946ef', // Fuchsia
  '#ec4899', // Pink
  '#f43f5e', // Rose
].toReversed()

// Background color classes for non-person profile types
// Each type/market gets a distinct amber shade to differentiate them
const profileTypeColorClasses: Record<string, string> = {
  job: 'bg-blue-500/85', // Blue with transparency
  market_job: 'bg-amber-400/90',
  market_applicant: 'bg-amber-400/90',
  market: 'bg-amber-400/90',
  market_company: 'bg-amber-400/90',
  market_company_job: 'bg-amber-400/90',
  company: 'bg-slate-500/85', // Slate with transparency
  team: 'bg-violet-500/85', // Violet with transparency
  org_group: 'bg-emerald-500/85', // Slate with transparency
  public: 'bg-emerald-500/85', // Slate with transparency
}

// Special color overrides for variants
const variantColorClasses: Record<string, string> = {
  job: 'bg-indigo-500/85', // Indigo for jobs
  org: 'bg-emerald-500/85',
}

// Subtle (resting) class strings used inside CollapsibleListItem rows.
// At rest the avatar uses a 10% tint of the entity color with muted text.
// When an ancestor `Item` (group/item) carries data-selected=true, the brand
// color is restored. Tailwind requires static class names — keep these literal.
const subtleClassMap: Record<string, string> = {
  indigo:
    'bg-indigo-500/10 [&>span]:text-indigo-400 dark:[&>span]:text-indigo-500 group-data-[selected=true]/item:bg-indigo-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  blue: 'bg-blue-500/10 [&>span]:text-blue-400 dark:[&>span]:text-blue-500 group-data-[selected=true]/item:bg-blue-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  violet:
    'bg-violet-500/10 [&>span]:text-violet-400 dark:[&>span]:text-violet-500 group-data-[selected=true]/item:bg-violet-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  emerald:
    'bg-emerald-500/10 [&>span]:text-emerald-400 dark:[&>span]:text-emerald-500 group-data-[selected=true]/item:bg-emerald-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  slate:
    'bg-slate-500/10 [&>span]:text-slate-400 dark:[&>span]:text-slate-500 group-data-[selected=true]/item:bg-slate-500/85 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
  amber:
    'bg-amber-400/10 [&>span]:text-amber-500 dark:[&>span]:text-amber-400 group-data-[selected=true]/item:bg-amber-400/90 group-data-[selected=true]/item:[&>span]:text-white dark:group-data-[selected=true]/item:[&>span]:text-white transition-colors',
}

const getSubtleColorName = (type?: ProfileType, variant?: string): string => {
  if (variant === 'job') return 'indigo'
  if (variant === 'org') return 'emerald'
  if (type === 'job') return 'blue'
  if (type === 'team') return 'violet'
  if (type === 'org_group' || type === 'public') return 'emerald'
  if (type === 'company') return 'slate'
  if (
    type === 'market_job' ||
    type === 'market_applicant' ||
    type === 'market' ||
    type === 'market_company' ||
    type === 'market_company_job'
  )
    return 'amber'
  return 'slate'
}

const hashString = (string: string) => {
  let hash = 0
  for (let i = 0; i < string.length; ++i) hash = string.charCodeAt(i) + ((hash << 5) - hash)
  return hash
}

const stringToColor = (string: string, isPerson: boolean) => {
  if (!isPerson) return undefined
  const hash = hashString(string)
  return personColors[Math.abs(hash % personColors.length)]
}

const getColorClass = (type?: ProfileType, variant?: string) => {
  // Check variant override first
  if (variant && variant in variantColorClasses) {
    return variantColorClasses[variant]
  }
  // Then check type
  if (type && type in profileTypeColorClasses) {
    return profileTypeColorClasses[type]
  }
  return 'bg-slate-500/85' // default to slate
}

const avatarVariants = cva(
  'relative flex shrink-0 overflow-hidden items-center justify-center font-medium text-white select-none rounded-md',
  {
    variants: {
      size: {
        'xs': 'size-5 text-xs rounded-full',
        'sm': 'size-6 text-sm rounded-sm',
        'md': 'size-8 text-lg',
        'lg': 'size-9 text-xl',
        'xl': 'size-10 text-2xl',
        '2xl': 'size-11 text-3xl',
        '3xl': 'size-12 text-4xl rounded-lg',
        '4xl': 'size-16 text-4xl rounded-lg',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
)

function withTooltip(element: React.ReactElement, tooltip?: ReactNode) {
  if (!tooltip) return element
  return (
    <Tooltip>
      <TooltipTrigger asChild>{element}</TooltipTrigger>
      <TooltipContent side="bottom" className="p-2">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

export function Avatar({
  size = 'lg',
  url,
  type,
  name,
  className,
  variant,
  forceInitials,
  subtle,
  tooltip,
  ...props
}: Omit<ComponentProps<'span'>, 'style'> & {
  size?: VariantProps<typeof avatarVariants>['size']
  url?: string | null
  type?: ProfileType
  name?: string | null
  variant?: 'job' | 'org'
  /** When true, render only initials — skip the type icon and the URL image. */
  forceInitials?: boolean
  /**
   * When true, render with a 10% tint of the entity color at rest and restore
   * the full brand color when an ancestor `Item` has data-selected=true.
   * Designed for avatars inside CollapsibleListItem rows.
   */
  subtle?: boolean
  tooltip?: ReactNode
}) {
  const { t } = useLingui()
  const isPerson = type === 'applicant' || type === 'member' || type === 'employee' || type === 'org_user'
  const id = `${isPerson ? 'applicant' : type}-${name}`
  const background = stringToColor(id, isPerson)
  const colorClass = !isPerson ? getColorClass(type, variant) : undefined
  const subtleClass = subtle && !isPerson ? subtleClassMap[getSubtleColorName(type, variant)] : undefined
  const initials = getInitials(name ?? '', type ?? '')

  // A real <img> layered over the fallback. We keep it INVISIBLE (opacity-0) until it successfully
  // loads, then reveal it — so a broken/404 image never paints the browser's broken-image glyph over
  // the fallback (the bug: it flashed on every mount before `onError` fired). A cached image is marked
  // loaded synchronously (it's already `complete` on mount, checked below) so it stays instant.
  const [errored, setErrored] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  // Layout effect (not useEffect) so the reveal of an already-loaded image happens BEFORE the browser
  // paints — a post-paint useEffect guarantees a one-frame initials flash on every mount, even for
  // cached/SSR images that are already `complete`.
  useIsomorphicLayoutEffect(() => {
    setErrored(false)
    setLoaded(false)
    // Cached/complete images don't fire onLoad after mount — reveal them immediately.
    const img = imgRef.current
    if (img?.complete && img.naturalWidth > 0) setLoaded(true)
  }, [url])

  // Get the appropriate icon component
  const getIconComponent = () => {
    // An applicant still registering has no image and no name yet — show the
    // generic applicant icon instead of empty initials.
    if (type === 'applicant' && !name) return NAVIGATION_ICONS.applicant
    if (type === 'market_job') return NAVIGATION_ICONS.marketOffer
    if (type === 'market_applicant') return NAVIGATION_ICONS.marketApplicant
    if (type === 'company') return null // Uses ImageCompany instead
    if (variant === 'job') return NAVIGATION_ICONS.offer
    if (type === 'job') return NAVIGATION_ICONS.job
    if (type === 'team') return NAVIGATION_ICONS.team
    if (type === 'org_group') return NAVIGATION_ICONS.orgGroup
    if (type === 'market') return NAVIGATION_ICONS.market
    if (type === 'market_company') return NAVIGATION_ICONS.market
    if (type === 'market_company_job') return NAVIGATION_ICONS.market
    return null
  }

  const IconComponent = getIconComponent()

  // Company logo / entity icon / initials on the colored background. This is the resting content for
  // image-less avatars AND the layer the <img> sits on top of while it loads or if it errors.
  // `forceInitials` shows only the initials (no icon, no image).
  const fallbackContent = forceInitials ? (
    <span className={cn(!isPerson && 'text-white text-xs font-bold')}>{initials}</span>
  ) : type === 'company' ? (
    <>
      <ImageCompany className="fill-white @max-[100px]/panel:hidden" width="45%" height="45%" />
      <span className="hidden text-xs font-bold text-white @max-[100px]/panel:block">{initials}</span>
    </>
  ) : IconComponent ? (
    <>
      <IconComponent className="text-white @max-[100px]/panel:hidden" width="45%" height="45%" />
      {/* When collapsed into the icon-rail (ancestor `@container/squash` ≤ 100px), show initials
          instead of the generic entity icon so items are distinguishable. */}
      <span className="hidden text-xs font-bold text-white @max-[100px]/panel:block">{initials}</span>
    </>
  ) : (
    <span className={cn(!isPerson && 'text-white font-bold')}>{initials}</span>
  )

  // While a real image is still loading we suppress the colored/initials fallback and show a neutral
  // placeholder instead — only revealing initials+color if the image is absent or actually errors. The
  // fallback used to flash for a frame before the in-flight image painted; this waits for the image.
  const showImage = !forceInitials && !!url && !errored
  const imagePending = showImage && !loaded

  return withTooltip(
    <span
      {...props}
      role="img"
      aria-label={t`User avatar`}
      className={cn(avatarVariants({ size }), imagePending ? 'bg-muted' : (subtleClass ?? colorClass), className)}
      style={!imagePending && background ? { background } : undefined}
    >
      {!imagePending && fallbackContent}
      {/* A plain <img> layered over the fallback (the root's overflow-hidden clips it to the avatar
          shape). The browser paints it directly — from the HTTP cache it's effectively instant, with
          no JS load-gating or loading state — so the colored/initials fallback below only shows while
          it loads or if it errors. Standard <img> + fallback layering (cf. MUI Avatar). */}
      {showImage && (
        <img
          ref={imgRef}
          src={url}
          alt=""
          aria-hidden
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={cn(
            'absolute inset-0 size-full rounded-[inherit] object-cover',
            // Hidden until it loads, so a broken/loading image never shows the broken-image glyph.
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </span>,
    tooltip,
  )
}
